import { useEffect, useState } from "react";
import axios from "axios";
import { Award, TrendingUp, Target, DollarSign } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PerformanceStats = ({ days = null }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const url = days && days > 0
          ? `${API}/stats?days=${days}`
          : `${API}/stats`;
        const response = await axios.get(url);
        setStats(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching stats:", error);
        setLoading(false);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 60000);

    return () => clearInterval(interval);
  }, [days]);

  if (loading) {
    return (
      <div className="glass-card rounded-xl p-6 h-48 flex items-center justify-center" data-testid="stats-loading">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!stats || stats.total_signals === 0) {
    return (
      <div className="glass-card rounded-xl p-6 border-white/10 text-center" data-testid="stats-empty">
        <p className="text-muted-foreground">No performance data available yet. Generate some signals to see statistics!</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="performance-stats">
      {/* Accuracy Rate */}
      <div className="glass-card rounded-xl p-6 border-white/10 hover:border-primary/50 transition-all animate-fade-in-up" data-testid="stat-accuracy">
        <div className="flex items-start justify-between mb-4">
          <div className="p-2 rounded-lg bg-[#00FF94]/10">
            <Target className="w-5 h-5 text-[#00FF94]" />
          </div>
          <span className="text-xs text-muted-foreground">Accuracy</span>
        </div>
        <div className="text-3xl font-bold text-data mb-1">{stats.accuracy_rate}%</div>
        <div className="text-xs text-muted-foreground">
          {stats.correct_signals} / {stats.total_signals} signals correct
        </div>
      </div>

      {/* Total Profit/Loss */}
      <div className="glass-card rounded-xl p-6 border-white/10 hover:border-primary/50 transition-all animate-fade-in-up" data-testid="stat-total-pl">
        <div className="flex items-start justify-between mb-4">
          <div className="p-2 rounded-lg bg-primary/10">
            <DollarSign className="w-5 h-5 text-primary" />
          </div>
          <span className="text-xs text-muted-foreground">Total P/L</span>
        </div>
        <div className={`text-3xl font-bold text-data mb-1 ${
          stats.total_profit_loss > 0 ? "text-[#00FF94]" : "text-[#FF0055]"
        }`}>
          {stats.total_profit_loss > 0 ? "+" : ""}${stats.total_profit_loss.toFixed(2)}
        </div>
        <div className="text-xs text-muted-foreground">Across all signals</div>
      </div>

      {/* Average P/L */}
      <div className="glass-card rounded-xl p-6 border-white/10 hover:border-primary/50 transition-all animate-fade-in-up" data-testid="stat-avg-pl">
        <div className="flex items-start justify-between mb-4">
          <div className="p-2 rounded-lg bg-[#FFD600]/10">
            <TrendingUp className="w-5 h-5 text-[#FFD600]" />
          </div>
          <span className="text-xs text-muted-foreground">Avg P/L</span>
        </div>
        <div className={`text-3xl font-bold text-data mb-1 ${
          stats.avg_profit_loss > 0 ? "text-[#00FF94]" : "text-[#FF0055]"
        }`}>
          {stats.avg_profit_loss > 0 ? "+" : ""}${stats.avg_profit_loss.toFixed(2)}
        </div>
        <div className="text-xs text-muted-foreground">Per signal</div>
      </div>

      {/* Best Signal */}
      <div className="glass-card rounded-xl p-6 border-white/10 hover:border-primary/50 transition-all animate-fade-in-up" data-testid="stat-best">
        <div className="flex items-start justify-between mb-4">
          <div className="p-2 rounded-lg bg-[#00FF94]/10">
            <Award className="w-5 h-5 text-[#00FF94]" />
          </div>
          <span className="text-xs text-muted-foreground">Best Signal</span>
        </div>
        <div className="text-2xl font-bold text-data mb-1">
          {stats.best_signal.coin} {stats.best_signal.signal}
        </div>
        <div className="text-sm text-[#00FF94] font-semibold">
          +${stats.best_signal.profit_loss.toFixed(2)} ({stats.best_signal.price_change_percent}%)
        </div>
      </div>
    </div>
  );
};

export default PerformanceStats;